import React, { useState, useEffect } from 'react';
import { apiGet } from '../utils/apiUtils';
import { FiX, FiCheckCircle, FiXCircle, FiMinus, FiAlertCircle, FiChevronDown, FiChevronUp, FiCode, FiCheck, FiChevronRight, FiAward, FiTarget, FiHelpCircle, FiCopy } from 'react-icons/fi';
import Editor from '@monaco-editor/react';
import UserAvatar from './UserAvatar';

/**
 * @file StudentTestResultsModal.jsx
 * @description Modal component used by students to view their detailed test results.
 *              Displays a list of attempted questions, their correctness, scored marks, 
 *              and specific code snippets if applicable.
 * @module components/StudentTestResultsModal
 */

/* ══════════════════════════════════════════════════════════════
 * HELPERS & UTILS
 * ══════════════════════════════════════════════════════════════ */

/**
 * ImprovedCodeSnippet Component.
 * 
 * @param {Object} props
 * @returns {JSX.Element}
 */
const ImprovedCodeSnippet = ({ code, language = 'cpp', maxHeight = '300px' }) => {
    const [copied, setCopied] = useState(false);

    const handleCopy = (e) => {
        e.stopPropagation();
        navigator.clipboard.writeText(code);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
    };

    const lineCount = code.split('\n').length;
    const lineHeight = 24;
    const paddingOffset = 42; // More generous padding
    const minHeight = 85;
    const estimatedHeight = Math.max(minHeight, Math.min(parseInt(maxHeight), (lineCount * lineHeight) + paddingOffset));

    const extMap = {
        'cpp': 'cpp',
        'java': 'java',
        'python': 'py',
        'javascript': 'js',
        'c': 'c'
    };

    const filename = `main.${extMap[language.toLowerCase()] || language}`;

    return (
        <div className="flex flex-col rounded border border-neutral-700 bg-neutral-800 overflow-hidden mt-4 mb-4 font-mono cursor-not-allowed group">
            <style>
                {`
                    .monaco-editor .view-line, 
                    .monaco-editor .view-lines,
                    .monaco-editor .margin-view-overlays,
                    .monaco-editor .view-content {
                        cursor: not-allowed !important;
                    }
                `}
            </style>
            <div className="bg-neutral-800/50 px-2 py-2 border-b border-neutral-700">
                <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-3">
                        <div className="flex space-x-1.5 text-not-allowed">
                            <div className="w-3 h-3 rounded-full bg-[#ff5f56]"></div>
                            <div className="w-3 h-3 rounded-full bg-[#ffbd2e]"></div>
                            <div className="w-3 h-3 rounded-full bg-[#27c93f]"></div>
                        </div>
                        <span className="text-sm text-gray-400 font-mono">
                            {filename}
                        </span>
                    </div>
                </div>
            </div>

            <div style={{ height: estimatedHeight + 'px' }} className="py-2 cursor-not-allowed">
                <Editor
                    height="100%"
                    theme="vs-dark"
                    language={language.toLowerCase()}
                    value={code}
                    options={{
                        readOnly: true,
                        domReadOnly: true,
                        fontSize: 13,
                        lineHeight: lineHeight,
                        minimap: { enabled: false },
                        scrollBeyondLastLine: false,
                        lineNumbers: 'on',
                        lineNumbersMinChars: 3,
                        lineDecorationsWidth: 40,
                        fontFamily: "'Fira Code', 'Cascadia Code', 'Ubuntu Mono', monospace",
                        renderLineHighlight: 'none',
                        scrollbar: {
                            verticalScrollbarSize: 6,
                            horizontalScrollbarSize: 6,
                            useShadows: false
                        },
                        padding: { top: 16, bottom: 16 },
                        folding: false,
                        automaticLayout: true,
                        contextmenu: false
                    }}
                />
            </div>
        </div>
    );
};

/* ══════════════════════════════════════════════════════════════
 * MAIN COMPONENT
 * ══════════════════════════════════════════════════════════════ */

/**
 * @component StudentTestResultsModal
 * @description Renders the comprehensive student view for detailed test results.
 * 
 * @param {Object} props
 * @param {Object} props.test - The test data object including the test ID and metadata.
 * @param {Function} props.onClose - Callback function to close the modal.
 * @returns {JSX.Element|null} The rendered Student Test Results Modal view.
 */
const StudentTestResultsModal = ({ test, onClose }) => {
    const [results, setResults] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [expandedQuestions, setExpandedQuestions] = useState(new Set());

    /* ══════════════════════════════════════════════════════════════
     * EFFECTS & HANDLERS
     * ══════════════════════════════════════════════════════════════ */

    const handleEditorDidMount = (editor, monaco) => {
        monaco.editor.defineTheme('mcq-light', {
            base: 'vs',
            inherit: true,
            rules: [
                { token: 'comment', foreground: '008000', fontStyle: 'italic' },
                { token: 'keyword', foreground: '0000FF' },
                { token: 'string', foreground: 'A31515' },
                { token: 'number', foreground: '098658' },
            ],
            colors: {
                'editor.background': '#f8fafc',
                'editor.foreground': '#1e293b',
                'editorLineNumber.foreground': '#94a3b8',
                'editor.selectionBackground': '#e2e8f0',
                'editor.lineHighlightBackground': '#f1f5f9'
            }
        });
        monaco.editor.setTheme('mcq-light');
    };

    const toggleQuestion = (questionId) => {
        setExpandedQuestions(prev => {
            const next = new Set();
            if (!prev.has(questionId)) {
                next.add(questionId);
            }
            return next;
        });
    };

    const renderQuestion = (q, index) => {
        let statusColor = 'text-slate-400'; // unattempted
        let StatusIcon = FiMinus;
        const isExpanded = expandedQuestions.has(q.id);

        let borderColor = 'border-l-amber-500'; // Default unattempted

        if (q.is_attempted) {
            const isCorrect = q.is_correct || q.status === 'Accepted';
            borderColor = isCorrect ? 'border-l-emerald-600' : 'border-l-red-600';

            if (isCorrect) {
                statusColor = 'text-emerald-600';
                StatusIcon = FiCheck;
            } else {
                statusColor = 'text-red-600';
                StatusIcon = FiX;
            }
        }

        return (
            <div key={q.id} className={`border-b border-b-gray-300 last:border-b-0 overflow-hidden border-l-4 ${borderColor}`}>
                <button
                    onClick={() => q.type === 'mcq' && toggleQuestion(q.id)}
                    className={`bg-accent w-full text-left p-5 flex items-center transition-all group ${q.type === 'mcq' ? 'cursor-pointer' : 'cursor-default'}`}
                >
                    <div className="flex-1 grid grid-cols-[1fr,200px,180px,40px] items-center gap-4">
                        {/* Title Column */}
                        <div className="flex items-center gap-3 overflow-hidden">
                            <span className="text-emerald-600 font-medium shrink-0">Q{index + 1}.</span>
                            <span className="text-slate-800 font-semibold text-base truncate pr-8" title={q.question_text}>
                                {q.question_text}
                            </span>
                        </div>

                        {/* Stats Columns - ONLY for coding questions */}
                        {q.type === 'code' ? (
                            <>
                                <div className="flex items-center gap-2 justify-start">
                                    <span className="text-slate-800 font-bold text-sm leading-none tabular-nums">
                                        {String(q.passed_count).padStart(2, '0')} / {String(q.total_count).padStart(2, '0')}
                                    </span>
                                    <span className="text-xs text-gray-500 font-semibold tracking-wide whitespace-nowrap">
                                        Test Cases Passed
                                    </span>
                                </div>
                                <div className="flex items-center gap-2 justify-start">
                                    <span className={`font-bold text-sm leading-none tabular-nums ${q.score > 0 ? 'text-emerald-600' : 'text-slate-500'}`}>
                                        {String(q.score).padStart(2, '0')} / {String(q.marks).padStart(2, '0')}
                                    </span>
                                    <span className="text-xs text-gray-500 font-semibold tracking-wide whitespace-nowrap">
                                        Marks Obtained
                                    </span>
                                </div>
                            </>
                        ) : (
                            <>
                                <div></div>
                                <div></div>
                            </>
                        )}

                        {/* Status Icon & Chevron Column */}
                        <div className="flex items-center justify-end gap-4">
                            <div className={`flex items-center ${statusColor} shrink-0`}>
                                <StatusIcon className="text-lg" />
                            </div>

                            {q.type === 'mcq' && (
                                <div className="text-slate-400 group-hover:text-emerald-600 transition-colors shrink-0">
                                    {isExpanded ? <FiChevronDown className="text-xl" /> : <FiChevronRight className="text-xl" />}
                                </div>
                            )}
                        </div>
                    </div>
                </button>

                {q.type === 'mcq' && (
                    <div
                        className={`grid transition-all duration-300 ease-in-out overflow-hidden ${isExpanded ? 'opacity-100' : 'opacity-0'}`}
                        style={{ gridTemplateRows: isExpanded ? '1fr' : '0fr' }}
                    >
                        <div className="min-h-0">
                            <div className="px-5 pb-5 pl-[4.25rem] space-y-6 bg-accent">
                                {q.question_code && (
                                    <div className="rounded-xl overflow-hidden shadow-sm border border-gray-200/50">
                                        <ImprovedCodeSnippet
                                            code={q.question_code}
                                            language={q.question_language || 'cpp'}
                                            maxHeight="400px"
                                        />
                                    </div>
                                )}

                                <div className="grid grid-cols-1 gap-2">
                                    {q.options.map((opt, optIndex) => {
                                        const isCorrectAnswer = optIndex === q.correct_option_index;
                                        const isUserAnswer = optIndex === q.selected_option_index;

                                        let optClass = "flex items-center px-4 py-3 text-sm rounded transition-all ";

                                        if (isCorrectAnswer) {
                                            optClass += "bg-emerald-600 text-white";
                                        } else if (isUserAnswer) {
                                            optClass += "bg-red-600 text-white";
                                        } else {
                                            optClass += "bg-white text-slate-700";
                                        }

                                        return (
                                            <div key={optIndex} className="space-y-2">
                                                <div className={optClass}>
                                                    <div className={`w-5 h-5 rounded-full flex items-center justify-center mr-3 shrink-0 ${isCorrectAnswer || isUserAnswer
                                                            ? 'bg-white'
                                                            : 'bg-gray-200'
                                                        }`}>
                                                        {isCorrectAnswer ? (
                                                            <FiCheck className="w-3 h-3 text-emerald-600" strokeWidth={5} />
                                                        ) : isUserAnswer ? (
                                                            <FiX className="w-3 h-3 text-red-600" strokeWidth={5} />
                                                        ) : null}
                                                    </div>
                                                    <span className="flex-1 font-medium text-left">{opt}</span>
                                                </div>

                                                {q.option_codes && q.option_codes[optIndex] && (
                                                    <div className="mb-2">
                                                        <ImprovedCodeSnippet
                                                            code={q.option_codes[optIndex]}
                                                            language={q.question_language || 'cpp'}
                                                            maxHeight="150px"
                                                        />
                                                    </div>
                                                )}
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        );
    };

    useEffect(() => {
        const fetchResults = async () => {
            try {
                const response = await apiGet(`/tests/${test.id}/my-results`);
                if (!response.ok) throw new Error('Failed to fetch personal results');
                const data = await response.json();
                setResults(data);
            } catch (err) {
                console.error(err);
                setError('Failed to load your test results.');
            } finally {
                setLoading(false);
            }
        };

        if (test?.id) fetchResults();
    }, [test?.id]);

    if (!test) return null;

    const totalQuestions = results?.questions?.length || 0;
    const attemptedCount = results?.questions?.filter(q => q.is_attempted).length || 0;
    const correctCount = results?.questions?.filter(q => q.is_correct).length || 0;
    const incorrectCount = attemptedCount - correctCount;
    const unattemptedCount = totalQuestions - attemptedCount;

    const percentage = results?.total_marks > 0
        ? Math.round((results.total_score / results.total_marks) * 100)
        : 0;

    return (
        <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-emerald-950/50 backdrop-blur-md overflow-y-auto">
            <div className="bg-white w-full max-w-7xl mx-auto my-8 flex flex-col max-h-[85vh] shadow-2xl rounded">

                {/* Header - EXACT SAME as Admin Modal */}
                <div className="flex items-start justify-between p-6 py-4 bg-emerald-600 text-left rounded-t shrink-0">
                    <div>
                        <h3 className="text-xl font-bold text-white mb-0.5">{test.title}</h3>
                    </div>
                    <button onClick={onClose} className="text-emerald-100 hover:text-white transition-colors mt-1">
                        <FiX className="text-2xl" />
                    </button>
                </div>

                {/* Summary Stats Grid - OUTSIDE scrollable area to fix scrollbar position */}
                {!loading && !error && results && (
                    <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 pr-10 pl-7 py-6 border-b border-gray-300 bg-accent shrink-0">
                        <div className="flex items-center gap-3 rounded justify-start">
                            <p className="text-sm text-muted-foreground font-medium">Your Score:</p>
                            <p className="text-sm font-bold text-emerald-700">{results.total_score} <span className="text-sm text-muted-foreground font-normal">/ {results.total_marks}</span></p>
                        </div>
                        <div className="flex items-center gap-3 rounded justify-center">
                            <p className="text-sm text-muted-foreground font-medium">Correct:</p>
                            <p className="text-sm font-bold text-emerald-600">{correctCount}</p>
                        </div>
                        <div className="flex items-center gap-3 rounded justify-center">
                            <p className="text-sm text-muted-foreground font-medium">Incorrect:</p>
                            <p className="text-sm font-bold text-red-600">{incorrectCount}</p>
                        </div>
                        <div className="flex items-center gap-3 rounded justify-center">
                            <p className="text-sm text-muted-foreground font-medium">Unattempted:</p>
                            <p className="text-sm font-bold text-amber-600">{unattemptedCount}</p>
                        </div>
                        <div className="flex items-center gap-3 rounded justify-end">
                            <p className="text-sm text-muted-foreground font-medium">Percentage:</p>
                            <p className="text-sm font-bold text-slate-800">{percentage}%</p>
                        </div>
                    </div>
                )}

                {/* Content - Scrollable */}
                <div className="flex-1 overflow-y-auto custom-scrollbar flex flex-col">
                    {loading ? (
                        <div className="p-8 animate-pulse space-y-8">
                            <div className="space-y-4">
                                {[1, 2, 3].map(i => (
                                    <div key={i} className="h-32 bg-gray-100 rounded"></div>
                                ))}
                            </div>
                        </div>
                    ) : error ? (
                        <div className="m-8 p-10 text-red-600 text-center bg-red-50 border border-red-100 rounded flex flex-col items-center">
                            <FiAlertCircle className="text-4xl mb-3" />
                            <p className="font-bold text-lg">{error}</p>
                        </div>
                    ) : (
                        <>
                            <div className="p-8 pb-12">
                                {results.violation_count > 0 && (
                                    <div className="mb-8 p-4 rounded bg-accent border-l-4 border-red-600 flex items-center gap-3 text-red-700">
                                        {/* <FiAlertCircle className="text-xl shrink-0" /> */}
                                        <p className="text-sm font-medium">
                                            <strong>Proctoring Notice:</strong> {results.violation_count} security violation(s) (tab switching/exit) were recorded during this test attempt.
                                        </p>
                                    </div>
                                )}

                                {results.not_attempted ? (
                                    <div className="text-center py-20 bg-accent border-2 border-dashed border-gray-300 rounded">
                                        <h3 className="text-2xl font-bold text-gray-600 mb-2">Test Not Attempted</h3>
                                        <p className="text-gray-500 max-w-sm mx-auto">
                                            You didn't submit an attempt for this test. <br></br> Detailed breakdown is unavailable.
                                        </p>
                                    </div>
                                ) : (
                                    <div className="bg-white rounded shadow-sm overflow-hidden">
                                        {results.questions?.map((q, idx) => renderQuestion(q, idx))}
                                    </div>
                                )}
                            </div>
                        </>
                    )}
                </div>
            </div>
        </div>
    );
};

export default StudentTestResultsModal;
